import { ReactNode } from "react";

import { Edit } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";

import { StackFormContext } from "../context";
import { StackCreationWizardStep } from "../types";
import { useNewStackAnalyticsSegmentEvent } from "../useNewStackAnalyticsSegmentEvent";
import useStackCreationAnalyticsVersion from "../useStackCreationAnalyticsVersion";

type NewStackSummaryPanelProps = {
  name: string;
  step: StackCreationWizardStep;
  children: ReactNode;
  goToStepDisabled?: boolean;
  count?: number;
  fullbleed?: boolean;
};

const NewStackSummaryPanel = ({
  name,
  step,
  children,
  count,
  goToStepDisabled,
  fullbleed,
}: NewStackSummaryPanelProps) => {
  const { setCurrentStep } = useTypedContext(StackFormContext);
  const [isCollapsed, toggle] = useToggle(false);
  const trackSegmentEvent = useNewStackAnalyticsSegmentEvent();
  const analyticsVersion = useStackCreationAnalyticsVersion();

  const goToStep = () => {
    trackSegmentEvent("Summary Edit Click", {
      step: name,
    });
    setCurrentStep(step);
  };

  return (
    <CardCollapsible
      ariaLevel={3}
      ariaLabel={name}
      title={name}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={count !== undefined ? <Counter count={count} /> : undefined}
      headerSuffix={
        <ButtonIcon disabled={goToStepDisabled} icon={Edit} onPress={goToStep} variant="ghost">
          Edit step
        </ButtonIcon>
      }
      analyticsPage={AnalyticsPageStack.StackNew}
      analyticsProps={{ version: analyticsVersion, step: name }}
      analyticsTitle="Summary section toggle"
      fullbleed={fullbleed}
    >
      {children}
    </CardCollapsible>
  );
};

export default NewStackSummaryPanel;
